import { useDebounce } from "@api";
import { ResponsiveButton } from "@data-display/wrapper/ResponsiveButton";
import { ResponsiveFloatingButtons } from "@data-display/wrapper/ResponsiveFloatingButtons";
import { ResponsiveButtonRegister } from "@data-display/wrapper/ResponsiveFloatingButtons/buttonRegister";
import { ResponsiveButtonProvider } from "@data-display/wrapper/ResponsiveFloatingButtons/context";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import {
  type Breakpoint,
  Button,
  type ButtonProps,
  InputAdornment,
  Stack,
  type StackProps,
  type SvgIconProps,
  type SvgIconTypeMap,
  TextField,
  type Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import { type ReactNode, useEffect, useState } from "react";
import { DashboardHeaderStack } from "./styles";

export interface IDashboardHeaderProps {
  Icon?: OverridableComponent<SvgIconTypeMap<unknown, "svg">> & {
    muiName: string;
  };
  children?: ReactNode;
  onReset: () => void;
  search?: string;
  onSearch: (value: string) => void;
  searchPlaceholder: string;
  title?: string;
  actions?: ButtonProps[];
  breakpoint?: Breakpoint;
  slotProps?: {
    container?: Partial<StackProps>;
    icon?: Partial<SvgIconProps>;
  };
  display?: "default" | "compact";
  cancel?: () => void;
}

export const DashboardHeader = ({
  Icon,
  title,
  actions,
  children,
  onReset,
  search,
  onSearch,
  searchPlaceholder,
  slotProps,
  breakpoint = "tablet",
  display = "default",
  cancel,
}: IDashboardHeaderProps) => {
  const breakpointReached = useMediaQuery((theme: Theme) => theme.breakpoints.up(breakpoint));
  const [innerSearch, setInnerSearch] = useState(search);

  useEffect(() => setInnerSearch(search), [search]);

  const onDebounce = useDebounce<[string]>(
    (value) => {
      if (value.length >= 2 || value.length === 0) {
        onSearch(value);
      }
    },
    750,
    []
  );

  const renderResetButton = () => (
    <Button
      variant="text"
      onClick={onReset}
      startIcon={<SettingsBackupRestoreIcon />}
      sx={{ textDecoration: "underline", textDecorationThickness: "2px", minWidth: "unset" }}
    >
      Réinitialiser
    </Button>
  );

  const renderChildrenAndReset = () => {
    if (display === "compact") {
      return (
        <>
          {children && (
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 1,
              }}
            >
              {children}
            </Stack>
          )}
          {breakpointReached && renderResetButton()}
        </>
      );
    }
    return breakpointReached
      ? renderResetButton()
      : children && (
          <Stack
            direction="row"
            sx={{
              gap: 1,
            }}
          >
            {children}
          </Stack>
        );
  };

  return (
    <ResponsiveButtonProvider breakpoint={breakpoint}>
      <DashboardHeaderStack
        width="100%"
        gap={1}
        bgcolor={(theme) => theme.palette.surface.main}
        breakpoint={breakpoint}
        {...slotProps?.container}
      >
        {Icon && title && (
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: "center",
            }}
          >
            <Icon {...slotProps?.icon} />
            <Typography variant="h1">{title}</Typography>
          </Stack>
        )}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Stack
            direction="row"
            sx={[
              {
                alignItems: "center",
                gap: 1,
              },
              (theme) => ({
                flex: "1",
                [theme.breakpoints.up(breakpoint)]: {
                  flex: display === "default" ? "0 1 60%" : 1,
                },
              }),
            ]}
          >
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="secondary" />
                    </InputAdornment>
                  ),
                  sx: {
                    height: display === "default" ? "40px" : "32px",
                  },
                },
                htmlInput: { "aria-label": "search" },
              }}
              value={innerSearch ?? ""}
              onChange={(e) => {
                cancel?.();
                setInnerSearch(e.target.value);
                onDebounce(e.target.value);
              }}
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                "& .MuiInputBase-input": {
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                },
              }}
            />
            {renderChildrenAndReset()}
          </Stack>
          {actions && actions.length > 0 && (
            <Stack
              direction="row"
              sx={{
                gap: 2,
                ml: 2,
              }}
            >
              {actions.map((action, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                <ResponsiveButtonRegister key={index} position={index + 1}>
                  <ResponsiveButton {...action} breakpoint={breakpoint} />
                </ResponsiveButtonRegister>
              ))}
            </Stack>
          )}
        </Stack>
        {display === "default" && breakpointReached && children && (
          <Stack
            direction="row"
            sx={{
              gap: 1,
            }}
          >
            {children}
          </Stack>
        )}
      </DashboardHeaderStack>
      <ResponsiveFloatingButtons />
    </ResponsiveButtonProvider>
  );
};
